import React, { useState, useEffect, useMemo, useCallback } from "react";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "../ui/dialog";
import { Input } from "../ui/input";
import { Label } from "../ui/label";
import { Button } from "../ui/button";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../ui/select";
import { Folder, FolderOpen } from "lucide-react";
import toast from "react-hot-toast";
import { useModal } from "../../context/ModalContext";

const MAX_SECTION_DEPTH = 2; // Maximum 2 levels of subsections allowed

const SectionFormModal = ({
  isOpen,
  onClose,
  onSubmit,
  initialData = null,
  projectId,
  sections = [],
  rootSectionId = null,
  rootSectionName = "Root",
  projectStructure = null,
  isFirstTimeFlow = false,
}) => {
  const { incrementModalCount, decrementModalCount } = useModal();

  const [formData, setFormData] = useState({
    name: "",
    description: "",
    parent_section_id: null,
  });

  // New state for first-time flow
  const [subsectionName, setSubsectionName] = useState("");
  const [targetLocation, setTargetLocation] = useState("parent"); // 'parent' or 'subsection'

  const [loading, setLoading] = useState(false);
  const [errors, setErrors] = useState({});

  // Determine modal context based on initialData
  const isCreatingSubsection = useMemo(() => {
    // If initialData has parent_section_id but no section_id, it's creating a subsection
    return (
      initialData && initialData.parent_section_id && !initialData.section_id
    );
  }, [initialData]);

  const isEditingSection = useMemo(() => {
    // If initialData has section_id, it's editing an existing section
    return initialData && initialData.section_id;
  }, [initialData]);

  const isCreatingTopLevelSection = useMemo(() => {
    // If no initialData or initialData is empty/null, it's creating a top-level section
    return (
      !initialData ||
      (!initialData.section_id && !initialData.parent_section_id)
    );
  }, [initialData]);

  // Determine if this is MULTIPLE_TEST_SUITES structure (must be declared before useMemo/useEffect)
  const isMultiSuite =
    projectStructure === "MULTIPLE_TEST_SUITES" ||
    projectStructure === "MULTI_SUITE";

  // Check if sections exist - this determines if Parent Section field should be shown
  const hasSections = useMemo(() => {
    if (!Array.isArray(sections)) return false;
    // Flatten sections to check total count (including nested children)
    const flattenSections = (sectionList) => {
      let count = 0;
      sectionList.forEach((section) => {
        if (section) {
          count++;
          if (section.children && section.children.length > 0) {
            count += flattenSections(section.children);
          }
        }
      });
      return count;
    };
    return flattenSections(sections) > 0;
  }, [sections]);

  // Check if there will be actual options available (excluding the section being edited)
  const hasAvailableOptions = useMemo(() => {
    if (!Array.isArray(sections)) return false;

    const excludeId = initialData?.section_id;
    if (!excludeId) return hasSections; // Not editing, so all sections are available

    // Count sections excluding the one being edited
    const countAvailableSections = (sectionList) => {
      let count = 0;
      sectionList.forEach((section) => {
        if (section && String(section.section_id) !== String(excludeId)) {
          count++;
          if (section.children && section.children.length > 0) {
            count += countAvailableSections(section.children);
          }
        }
      });
      return count;
    };

    return countAvailableSections(sections) > 0;
  }, [sections, initialData?.section_id, hasSections]);

  // Determine if parent section field should be visible
  // Show it if:
  // 1. There are existing sections to choose from, OR
  // 2. We're creating a subsection (initialData has parent_section_id but no section_id)
  const shouldShowParentField = useMemo(() => {
    // Hide parent selection in first-time flow as we are creating the first section
    if (isFirstTimeFlow) return false;

    // Always show when creating a subsection (has parent_section_id but no section_id)
    if (isCreatingSubsection) {
      return true;
    }

    // For editing or creating sections, show if there are available options
    // (this accounts for excluding the section being edited)
    return hasAvailableOptions;
  }, [hasAvailableOptions, isCreatingSubsection, isFirstTimeFlow]);

  useEffect(() => {
    if (isOpen) {
      // Register modal with context
      incrementModalCount();

      // Reset first-time flow state
      setSubsectionName("");
      setTargetLocation("parent");

      // Only treat as editing if initialData has a section_id (existing section)
      // If initialData only has parent_section_id, it's a create operation with a parent
      if (initialData && initialData.section_id) {
        // Editing existing section
        setFormData({
          name: initialData.name || "",
          description: initialData.description || "",
          parent_section_id: initialData.parent_section_id || null,
        });
      } else {
        // Creating new section - use parent_section_id from initialData if provided
        setFormData({
          name: "",
          description: "",
          parent_section_id: initialData?.parent_section_id || null,
        });
      }
      setErrors({});

      return () => {
        // Unregister modal with context when closing
        decrementModalCount();
      };
    }
  }, [isOpen, initialData, incrementModalCount, decrementModalCount]);

  const handleChange = (name, value) => {
    // Ensure value is always a plain string or null (no React elements)
    let normalizedValue;
    if (name === "parent_section_id") {
      // Handle objects - extract section_id if it's an object
      if (typeof value === "object" && value !== null) {
        normalizedValue = value.section_id || value.id || null;
      } else {
        // Convert to string or null, ensuring no React elements
        normalizedValue =
          value === "" || value === null || value === undefined
            ? null
            : String(value);
      }
    } else {
      normalizedValue =
        value === "" ? null : typeof value === "string" ? value : String(value);
    }

    setFormData((prev) => ({
      ...prev,
      [name]: normalizedValue,
    }));
    // Clear error when user starts typing
    if (errors[name]) {
      setErrors((prev) => ({
        ...prev,
        [name]: "",
      }));
    }
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    handleChange(name, value);
  };

  // Determine if creating a top-level suite (no parent) or a section (has parent)
  // Only treat as editing if initialData has a section_id
  const isEditing = initialData && initialData.section_id;

  // Get appropriate labels
  const getEntityName = () => {
    if (isMultiSuite) {
      if (isEditingSection) {
        // When editing, check if the section being edited has a parent
        return initialData?.parent_section_id ? "Section" : "Suite";
      }
      return isCreatingTopLevelSection
        ? "Suite"
        : isCreatingSubsection
          ? "Section"
          : "Suite";
    }
    return "Section";
  };

  const getParentLabel = () => {
    if (isMultiSuite) {
      return isCreatingSubsection ? "Parent Suite" : "Parent Section";
    }
    return "Parent Section";
  };

  const getModalTitle = () => {
    if (isFirstTimeFlow) {
      return "Create First Section";
    }
    if (isEditingSection) {
      const entityName = isMultiSuite
        ? initialData?.parent_section_id
          ? "Section"
          : "Suite"
        : "Section";
      return `Edit ${entityName}`;
    }
    if (isCreatingSubsection) {
      return isMultiSuite ? "Create Section" : "Create Subsection";
    }
    // isCreatingTopLevelSection
    return isMultiSuite ? "Create Suite" : "Create Section";
  };

  const getFieldLabel = () => {
    if (isFirstTimeFlow) {
      return "Section Name";
    }
    if (isEditingSection) {
      return isMultiSuite
        ? initialData?.parent_section_id
          ? "Section Name"
          : "Suite Name"
        : "Section Name";
    }
    if (isCreatingSubsection) {
      return isMultiSuite ? "Section Name" : "Subsection Name";
    }
    // isCreatingTopLevelSection
    return isMultiSuite ? "Suite Name" : "Section Name";
  };

  const parentLabel = getParentLabel();
  const modalTitle = getModalTitle();
  const fieldLabel = getFieldLabel();

  const validate = () => {
    const newErrors = {};
    if (!formData.name || !formData.name.trim()) {
      newErrors.name = `${fieldLabel.replace(" *", "")} is required`;
    }

    // Validate subsection if enabled in first-time flow
    if (isFirstTimeFlow && targetLocation === "subsection") {
      if (!subsectionName || !subsectionName.trim()) {
        newErrors.subsectionName =
          "Subsection name is required when selected as target";
      }
    }

    return newErrors;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    const validationErrors = validate();
    if (Object.keys(validationErrors).length > 0) {
      setErrors(validationErrors);
      return;
    }

    setLoading(true);
    try {
      // Create clean payload with only plain data (no React elements, circular refs, etc.)
      // Ensure parent_section_id is a string or null, not an object
      let cleanParentSectionId = null;
      if (formData.parent_section_id) {
        if (
          typeof formData.parent_section_id === "object" &&
          formData.parent_section_id !== null
        ) {
          // If it's an object, extract the ID
          cleanParentSectionId =
            formData.parent_section_id.section_id ||
            formData.parent_section_id.id ||
            null;
        } else {
          cleanParentSectionId = String(formData.parent_section_id);
        }
      }

      const cleanPayload = {
        name: formData.name ? String(formData.name).trim() : "",
        description: formData.description
          ? String(formData.description).trim()
          : null,
        parent_section_id: cleanParentSectionId,
        parent_folder_id: cleanParentSectionId, // Map to folder_id for API compatibility
      };

      // Only include initialData properties if editing
      if (initialData && initialData.section_id) {
        cleanPayload.section_id = initialData.section_id;
      }

      // Add first-time flow specific data
      if (isFirstTimeFlow) {
        cleanPayload.subsectionName = subsectionName
          ? String(subsectionName).trim()
          : null;
        cleanPayload.targetLocation = targetLocation; // 'parent' or 'subsection'
      }

      await onSubmit(cleanPayload);
      onClose();
    } catch (error) {
      console.error("Section form submission error:", error);
      // Extract the meaningful error message
      const errorMessage = error.message || "Failed to save section";

      // Display error to user
      toast.error(errorMessage);

      // If it's a depth-related error, also set field-level error for better UX
      if (
        errorMessage.includes("levels of sections") ||
        errorMessage.includes("Maximum section depth")
      ) {
        setErrors({
          parent_section_id: errorMessage,
        });
      }
    } finally {
      setLoading(false);
    }
  };

  // Build flat list of sections excluding the current section (if editing) and its descendants
  const buildSectionOptions = useCallback(
    (sectionList, excludeId = null, level = 0) => {
      const options = [];
      sectionList.forEach((section) => {
        if (!section || !section.section_id) {
          return; // Skip invalid sections
        }

        // Ensure section_id is a string and exclude the current section if editing
        const sectionId = String(section.section_id);
        if (sectionId === excludeId) {
          return; // Skip the section being edited
        }

        const indent = "  ".repeat(level);
        const displayName = section.display_name || section.name;
        const segments = (section.path || "")
          .split("/")
          .map((segment) => segment.trim())
          .filter(Boolean);
        const depth = Math.max(0, segments.length - 1);
        const canAcceptChildren = depth < MAX_SECTION_DEPTH;

        options.push({
          value: sectionId, // Always a string
          label: `${indent}${displayName}`, // Removed emoji icon
          section,
          depth,
          canAcceptChildren,
        });

        if (
          section.children &&
          Array.isArray(section.children) &&
          section.children.length > 0
        ) {
          options.push(
            ...buildSectionOptions(section.children, excludeId, level + 1),
          );
        }
      });
      return options;
    },
    [],
  );

  // Build section options - only include actual sections (no root/default options)
  // Note: "None (Top Level)" option is added separately in SelectContent, not here
  const sectionOptions = useMemo(() => {
    // If we're creating a subsection (has parent_section_id but no section_id),
    // we should still try to build options even if hasSections is false
    // This handles edge cases where sections array might be empty but parent exists
    const isCreatingSubsectionCheck =
      initialData && initialData.parent_section_id && !initialData.section_id;

    if (!hasSections && !isCreatingSubsectionCheck) {
      return [];
    }

    // Add all actual sections (no root section special handling)
    // Don't add "None (Top Level)" here - it's added explicitly in SelectContent
    const options = buildSectionOptions(
      sections || [],
      initialData?.section_id,
    );

    return options;
  }, [sections, initialData?.section_id, buildSectionOptions, hasSections]);

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="sm:max-w-[500px]">
        <DialogHeader>
          <DialogTitle>{modalTitle}</DialogTitle>
        </DialogHeader>
        <form onSubmit={handleSubmit} autoComplete="off">
          <div className="space-y-4 py-4">
            {isFirstTimeFlow && (
              <div className="bg-blue-50 p-3 rounded-md mb-4 text-sm text-blue-800">
                To create a test case, you first need a section. You can create
                a parent section and optionally a subsection.
              </div>
            )}

            <div>
              <Label htmlFor="name">{fieldLabel} *</Label>
              <Input
                id="name"
                name="name"
                value={formData.name}
                onChange={handleInputChange}
                className={errors.name ? "border-red-500" : ""}
                placeholder="e.g., Authentication"
                required
                autoFocus
              />
              {errors.name && (
                <p className="text-sm text-red-500 mt-1">{errors.name}</p>
              )}
            </div>

            {isFirstTimeFlow && (
              <>
                <div className="pt-2 border-t mt-4">
                  <Label htmlFor="subsectionName">
                    Subsection Name (Optional)
                  </Label>
                  <p className="text-xs text-gray-500 mb-2">
                    Create a sub-folder under the new section
                  </p>
                  <Input
                    id="subsectionName"
                    value={subsectionName}
                    onChange={(e) => {
                      setSubsectionName(e.target.value);
                      // Auto-select subsection as target if user types in it
                      if (e.target.value && targetLocation === "parent") {
                        setTargetLocation("subsection");
                      }
                    }}
                    placeholder="e.g., Login Flow"
                    className={errors.subsectionName ? "border-red-500" : ""}
                  />
                  {errors.subsectionName && (
                    <p className="text-sm text-red-500 mt-1">
                      {errors.subsectionName}
                    </p>
                  )}
                </div>

                <div className="pt-2">
                  <Label className="mb-2 block">Create Test Case In: *</Label>
                  <div className="space-y-2">
                    <div className="flex items-center space-x-2">
                      <input
                        type="radio"
                        id="target-parent"
                        name="targetLocation"
                        value="parent"
                        checked={targetLocation === "parent"}
                        onChange={() => setTargetLocation("parent")}
                        className="h-4 w-4 text-indigo-600 focus:ring-indigo-500 border-gray-300"
                      />
                      <Label
                        htmlFor="target-parent"
                        className="font-normal cursor-pointer"
                      >
                        Parent Section ({formData.name || "New Section"})
                      </Label>
                    </div>
                    <div className="flex items-center space-x-2">
                      <input
                        type="radio"
                        id="target-subsection"
                        name="targetLocation"
                        value="subsection"
                        checked={targetLocation === "subsection"}
                        onChange={() => setTargetLocation("subsection")}
                        disabled={!subsectionName}
                        className="h-4 w-4 text-indigo-600 focus:ring-indigo-500 border-gray-300 disabled:opacity-50"
                      />
                      <Label
                        htmlFor="target-subsection"
                        className={`font-normal cursor-pointer ${!subsectionName ? "text-gray-400" : ""}`}
                      >
                        Subsection ({subsectionName || "New Subsection"})
                      </Label>
                    </div>
                  </div>
                </div>
              </>
            )}

            <div>
              <Label htmlFor="description">Description</Label>
              <textarea
                id="description"
                name="description"
                value={formData.description}
                onChange={handleInputChange}
                rows={3}
                autoComplete="off"
                className="flex min-h-[80px] w-full rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50"
              />
            </div>

            {/* Parent Section Field - Show when sections exist or when creating a subsection */}
            {shouldShowParentField && (
              <div>
                <Label htmlFor="parent_section_id">
                  {parentLabel} {isMultiSuite ? "(Optional)" : "(Optional)"}
                </Label>
                <Select
                  value={
                    formData.parent_section_id
                      ? String(formData.parent_section_id)
                      : isMultiSuite && !initialData
                        ? "__none__"
                        : "__none__"
                  }
                  onValueChange={(value) => {
                    // Always allow changes - dropdown should be fully interactable
                    // Ensure value is always a plain string, convert __none__ to null
                    const cleanValue =
                      value === "__none__" || value === ""
                        ? null
                        : String(value);
                    handleChange("parent_section_id", cleanValue);
                  }}
                  disabled={false}
                >
                  <SelectTrigger
                    className={`${errors.parent_section_id ? "border-red-500" : ""}`}
                  >
                    {/* Icon rendered INSIDE SelectTrigger as part of selected value display - this is the ONLY icon */}
                    <div className="flex items-center gap-2 flex-1 min-w-0">
                      {formData.parent_section_id &&
                        (() => {
                          const selectedOption = sectionOptions.find((opt) => {
                            const optValue = opt.value ? String(opt.value) : "";
                            return (
                              optValue === String(formData.parent_section_id)
                            );
                          });
                          if (selectedOption) {
                            const isTopLevel =
                              selectedOption.isRoot ||
                              selectedOption.depth === 0;
                            const SelectedIcon = isTopLevel
                              ? FolderOpen
                              : Folder;
                            return (
                              <SelectedIcon className="w-4 h-4 text-gray-500 flex-shrink-0" />
                            );
                          }
                          return null;
                        })()}
                      {/* SelectValue extracts only text from SelectItem - no icon duplication */}
                      <SelectValue
                        placeholder={
                          isMultiSuite && !formData.parent_section_id
                            ? "None (Top Level)"
                            : `Select ${parentLabel.toLowerCase()} (Optional)`
                        }
                      />
                    </div>
                  </SelectTrigger>
                  <SelectContent>
                    {/* Always show "None (Top Level)" option when there are sections available
                        This allows users to change from a selected parent to no parent (top-level) */}
                    {sectionOptions.length > 0 && (
                      <SelectItem value="__none__">None (Top Level)</SelectItem>
                    )}
                    {
                      sectionOptions
                        .map((option) => {
                          // Ensure value is always a string, never an object, and never empty string
                          let optionValue = option.value
                            ? typeof option.value === "object" &&
                              option.value !== null
                              ? option.value.section_id ||
                                option.value.id ||
                                null
                              : String(option.value)
                            : null;

                          // Skip options with null/empty values - they shouldn't be in sectionOptions
                          // The "None (Top Level)" option is added separately above
                          if (!optionValue || optionValue === "") {
                            return null; // Skip this option
                          }

                          // Determine if this is a top-level section (no parent)
                          const isTopLevel =
                            option.isRoot || option.depth === 0;

                          return (
                            <SelectItem
                              key={optionValue}
                              value={optionValue}
                              disabled={!option.canAcceptChildren}
                            >
                              {/* NO ICON HERE - SelectValue extracts text only, icon appears ONLY in SelectTrigger */}
                              <span>{option.label}</span>
                              {!option.canAcceptChildren && (
                                <span className="ml-2 text-xs text-gray-400">
                                  (Max depth reached)
                                </span>
                              )}
                            </SelectItem>
                          );
                        })
                        .filter(Boolean) // Remove null entries
                    }
                  </SelectContent>
                </Select>
                {errors.parent_section_id && (
                  <p className="text-sm text-red-500 mt-1">
                    {errors.parent_section_id}
                  </p>
                )}
              </div>
            )}
          </div>
          <DialogFooter>
            <Button
              type="button"
              onClick={onClose}
              variant="outline"
              disabled={loading}
            >
              Cancel
            </Button>
            <Button type="submit" disabled={loading}>
              {loading
                ? "Saving..."
                : isFirstTimeFlow
                  ? "Save & Proceed"
                  : initialData && initialData.section_id
                    ? "Update"
                    : "Save"}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
};

export default SectionFormModal;
